import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import {
  useBusinessTicketHolderList,
  useBusinessManualReserveTicketList,
  type TicketHolderPet,
  type BusinessManualReserveTicketItem,
} from "~/services/business/reserve";

export const Route = createFileRoute("/business/layout/manual-reserve")({
  component: ManualReserveStart,
});

const PRODUCT_TYPE_LABEL: Record<string, string> = {
  PRODUCT_TYPE_KINDERGARTEN: "유치원",
  PRODUCT_TYPE_HOTEL: "호텔",
  PRODUCT_TYPE_DAYCARE: "데이케어",
  PRODUCT_TYPE_GROOMING: "미용",
};

function formatDate(d?: string) {
  if (!d) return "";
  return d.split(/[T ]/)[0].replace(/-/g, ".");
}

function ManualReserveStart() {
  const { history } = useRouter();
  const [selectedPet, setSelectedPet] = useState<TicketHolderPet | null>(null);

  const { data: holders = [], isLoading: isHolderLoading } =
    useBusinessTicketHolderList();

  // 선택된 반려동물 보호자의 이용권 목록 → 해당 반려동물 소유분만
  const { data: ticketList = [], isLoading: isTicketLoading } =
    useBusinessManualReserveTicketList({
      userSeq: selectedPet?.userSeq ?? 0,
    });

  const petTickets = useMemo(
    () =>
      ticketList.filter(
        (t: BusinessManualReserveTicketItem) =>
          t.petSeq === selectedPet?.petSeq && t.remainCount > 0
      ),
    [ticketList, selectedPet]
  );

  const goReserve = (ticket: BusinessManualReserveTicketItem) => {
    if (!selectedPet) return;
    history.push("/business/layout/reservation-with-issued-ticket", {
      userSeq: selectedPet.userSeq,
      purchaseTicketSeq: ticket.purchaseTicketSeq,
      cdProductType: ticket.cdProductType,
      ticketName: ticket.name,
      remainQty: ticket.remainCount,
      qty: ticket.totalCount,
      userName: selectedPet.userName,
    });
  };

  return (
    <>
      <LayoutHeader
        title="수기 예약"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={() => {
          if (selectedPet) {
            setSelectedPet(null); // 이용권 선택 단계에서는 반려동물 선택으로 복귀
            return;
          }
          window.history.back();
        }}
      />
      <div className="flex-auto bg-[#F5F5F5]">
        <div className="flex flex-col gap-3 p-4">
          {!selectedPet ? (
            <>
              <div className="text-sm font-bold">
                예약할 반려동물을 선택하세요
              </div>
              {isHolderLoading ? (
                <div className="flex justify-center py-16">
                  <Loading visible={true} />
                </div>
              ) : holders.length === 0 ? (
                <div className="rounded-xl bg-white p-6 text-center text-sm text-gray">
                  사용 가능한 이용권을 보유한 반려동물이 없습니다.
                  <br />
                  이용권 발급 후 이용해주세요.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {holders.map((pet) => (
                    <button
                      key={`${pet.userSeq}-${pet.petSeq}`}
                      type="button"
                      onClick={() => setSelectedPet(pet)}
                      className="flex items-center gap-3 rounded-xl bg-white p-3 text-left"
                    >
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-[#F5F5F5]">
                        {pet.thumbnailUrl && (
                          <img
                            src={pet.thumbnailUrl}
                            alt={pet.petName}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold">
                          {pet.petName}
                        </div>
                        <div className="truncate text-xs text-gray">
                          보호자 {pet.userName}
                        </div>
                      </div>
                      <span className="shrink-0 rounded-full bg-[#FFF3E5] px-2.5 py-1 text-xs font-bold text-yellow">
                        이용권 {pet.ticketCount}개
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* 선택된 반려동물 요약 */}
              <div className="flex items-center gap-3 rounded-xl border border-yellow bg-white p-3">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-[#F5F5F5]">
                  {selectedPet.thumbnailUrl && (
                    <img
                      src={selectedPet.thumbnailUrl}
                      alt={selectedPet.petName}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold">
                    {selectedPet.petName}
                  </div>
                  <div className="truncate text-xs text-gray">
                    보호자 {selectedPet.userName}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPet(null)}
                  className="shrink-0 text-xs text-gray underline"
                >
                  변경
                </button>
              </div>

              <div className="text-sm font-bold">
                사용할 이용권을 선택하세요
              </div>
              {isTicketLoading ? (
                <div className="flex justify-center py-16">
                  <Loading visible={true} />
                </div>
              ) : petTickets.length === 0 ? (
                <div className="rounded-xl bg-white p-6 text-center text-sm text-gray">
                  이 반려동물이 사용할 수 있는 이용권이 없습니다.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {petTickets.map((ticket) => (
                    <button
                      key={ticket.purchaseTicketSeq}
                      type="button"
                      onClick={() => goReserve(ticket)}
                      className="flex items-center gap-3 rounded-xl bg-white p-3 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-bold">
                            {ticket.name}
                          </span>
                          {PRODUCT_TYPE_LABEL[ticket.cdProductType] && (
                            <span className="shrink-0 rounded bg-[#F5F5F5] px-1.5 py-0.5 text-[10px] text-gray">
                              {PRODUCT_TYPE_LABEL[ticket.cdProductType]}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-gray">
                          {formatDate(ticket.expiredDate)} 까지
                        </div>
                      </div>
                      <span className="shrink-0 text-sm font-bold text-yellow">
                        {ticket.remainCount}회 남음
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
